/**
 * Contact List Page
 * Master Data > Contact list with Vendor/Customer tabs
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Table, Button, Input, Dropdown, Badge, Tag, theme, App, Typography, Space } from 'antd';
import type { TableProps } from 'antd';
import {
  PlusOutlined,
  SearchOutlined,
  FilterOutlined,
  MoreOutlined,
  EditOutlined,
  CheckCircleOutlined,
  StopOutlined,
  InboxOutlined,
  CheckOutlined,
} from '@ant-design/icons';

const { Title } = Typography;
import {
  useMasterDataContactsTable,
  useMasterDataContactMutations,
} from '@/hooks/useMasterDataContacts';
import type {
  MasterDataContactListItem,
  MasterDataContactDetail,
  ContactType,
} from '@/types/api/masterDataContact';
import { masterDataContactApi } from '@/services/api/masterDataContact.api';
import { contactApi } from '@/services/api/contact.api';
import { customerApi } from '@/services/api/customer.api';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';
import { useMasterDataContactStore } from '@/stores/masterDataContactStore';
import MasterDataContactFormModal from './MasterDataContactFormModal';
import ContactFilterPopover from './ContactFilterPopover';
import { ConfirmationDialog, useConfirmationDialog } from '@/components/common/ConfirmationDialog';
import TableContainer from '@/components/common/TableContainer';
import { useVendorTranslation } from '@/hooks/useVendorTranslation';
import { useTranslation } from 'react-i18next';
import { TimestampDisplay } from '@/components/common/TimestampDisplay';
import { ContactAddressDisplay } from '@/components/common/ContactAddressDisplay';
import { HighlightText } from '@/components/common/HighlightText';

const ContactListPage = () => {
  const navigate = useNavigate();
  const { token } = theme.useToken();
  const { message } = App.useApp();
  const { t } = useVendorTranslation();
  const { t: tContacts } = useTranslation('contacts');

  // Use Zustand store for filters
  const { filters, setSearch, setTypeId, setIsPrimary, setIsActive } = useMasterDataContactStore();

  const [filterPopoverOpen, setFilterPopoverOpen] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingContact, setEditingContact] = useState<MasterDataContactDetail | null>(null);
  const [editingListRecord, setEditingListRecord] = useState<MasterDataContactListItem | null>(
    null
  );
  const [loadingContactDetail, setLoadingContactDetail] = useState(false);
  const [toggling, setToggling] = useState(false);
  const [existingPrimaryForSetPrimary, setExistingPrimaryForSetPrimary] = useState<{
    contactId: number;
    name: string;
  } | null>(null);

  // Fetch contacts using useAntdTable hook
  const { tableProps, isLoading, error, refresh } = useMasterDataContactsTable({
    search: filters.search,
    typeId: filters.typeId,
    isPrimary: filters.isPrimary,
    isActive: filters.isActive,
  });

  const {
    toggleVendorContactStatus,
    toggleCustomerContactStatus,
    setVendorContactPrimary,
    setCustomerContactPrimary,
  } = useMasterDataContactMutations();

  // Confirmation dialogs
  const toggleStatusDialog = useConfirmationDialog<MasterDataContactListItem>();
  const setPrimaryDialog = useConfirmationDialog<MasterDataContactListItem>();

  // Handle tab change
  const handleTabChange = (key: string) => {
    setTypeId(parseInt(key) as ContactType);
  };

  // Handle filter apply
  const handleFilterApply = (filterValues: {
    isPrimary: boolean | null;
    isActive: boolean | null;
  }) => {
    setIsPrimary(filterValues.isPrimary);
    setIsActive(filterValues.isActive);
  };

  // Handle toggle status
  const handleToggleStatus = (contact: MasterDataContactListItem) => {
    toggleStatusDialog.open(contact);
  };

  // Handle set primary - fetch existing primary contact first
  const handleSetPrimary = async (contact: MasterDataContactListItem) => {
    try {
      // Fetch existing contacts to find current primary
      let existingPrimary: { contactId: number; name: string } | null = null;

      if (contact.vendorId) {
        const response = await contactApi.list(contact.vendorId);
        const primary = response.data?.find(
          (c) => c.isPrimary && c.contactId !== contact.contactId
        );
        if (primary) {
          existingPrimary = { contactId: primary.contactId, name: primary.name };
        }
      } else if (contact.customerId) {
        const response = await customerApi.getContacts(contact.customerId);
        const primary = response.data?.find(
          (c) => c.isPrimary && c.contactId !== contact.contactId
        );
        if (primary) {
          existingPrimary = { contactId: primary.contactId, name: primary.name };
        }
      }

      setExistingPrimaryForSetPrimary(existingPrimary);
      setPrimaryDialog.open(contact);
    } catch (error) {
      console.error('Error fetching existing primary:', error);
      // Still open dialog even if fetch fails
      setExistingPrimaryForSetPrimary(null);
      setPrimaryDialog.open(contact);
    }
  };

  // Handle edit - fetch full contact detail before opening modal
  const handleEdit = async (record: MasterDataContactListItem) => {
    if (!record.isActive) return;

    setLoadingContactDetail(true);
    try {
      const entityId = record.vendorId || record.customerId;
      const type = record.vendorId ? 1 : 2;
      if (entityId) {
        const contactDetail = await masterDataContactApi.getById(
          record.contactId,
          entityId,
          type as ContactType
        );
        // Merge list record data with detail for country/state/city labels (for edit pre-fill fallback)
        const mergedContact: MasterDataContactDetail = {
          ...contactDetail,
          // List record may have resolved country/state/city from API
          country: record.country ?? contactDetail.country,
          state: record.state ?? contactDetail.state,
          city: record.city ?? contactDetail.city,
        };
        setEditingContact(mergedContact);
        setEditingListRecord(record);
        setModalVisible(true);
      }
    } catch (error) {
      console.error('Error fetching contact detail:', error);
      message.error(t('vendor.messages.fetchFailed'));
    } finally {
      setLoadingContactDetail(false);
    }
  };

  // Table columns
  const columns: TableProps<MasterDataContactListItem>['columns'] = [
    {
      title: tContacts('table.primary'),
      dataIndex: 'isPrimary',
      key: 'isPrimary',
      width: 120,
      align: 'center',
      sorter: true,
      showSorterTooltip: false,
      render: (isPrimary: boolean) =>
        isPrimary ? (
          <CheckOutlined style={{ color: token.colorSuccess, fontSize: token.sizeMD }} />
        ) : null,
    },
    {
      title: tContacts('table.name'),
      dataIndex: 'name',
      key: 'name',
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      render: (name: string) => (
        <span style={{ fontWeight: token.fontWeightStrong }}>
          <HighlightText text={name} searchTerm={filters.search} fallback="-" />
        </span>
      ),
    },
    {
      title: tContacts('table.jobTitle'),
      dataIndex: 'jobTitle',
      key: 'jobTitle',
      width: 150,
      sorter: true,
      showSorterTooltip: false,
      render: (jobTitle: string) => <HighlightText text={jobTitle} searchTerm={filters.search} fallback="-" />,
    },
    {
      title: tContacts('table.companyName'),
      dataIndex: 'companyName',
      key: 'companyName',
      width: 240,
      sorter: true,
      showSorterTooltip: false,
      // #92: ONE row per person — every affiliated company shows as a chip
      render: (companyName: string, record: MasterDataContactListItem) => {
        const affiliations = record.affiliations ?? [];
        if (affiliations.length === 0) {
          return <HighlightText text={companyName} searchTerm={filters.search} fallback="-" />;
        }
        return (
          <div className="tw-flex tw-flex-wrap tw-gap-1 tw-items-center">
            {affiliations.map((affiliation) => (
              <Tag
                key={`${affiliation.companyType}-${affiliation.companyId}`}
                bordered={false}
                color={affiliation.companyType === 'vendor' ? 'purple' : 'blue'}
                style={{ marginInlineEnd: 0 }}
              >
                {affiliation.companyName}
              </Tag>
            ))}
            {affiliations.length > 1 && (
              <span style={{ fontSize: token.fontSizeSM, color: token.colorTextSecondary }}>
                {tContacts('table.affiliationCount', { count: affiliations.length })}
              </span>
            )}
          </div>
        );
      },
    },
    {
      title: tContacts('table.phone'),
      dataIndex: 'phoneNumber',
      key: 'phoneNumber',
      width: 140,
      sorter: true,
      showSorterTooltip: false,
      render: (phone: string) => phone || '-',
    },
    {
      title: tContacts('table.mobile'),
      dataIndex: 'mobile',
      key: 'mobile',
      width: 140,
      render: (mobile: string) => mobile || '-',
    },
    {
      title: tContacts('table.fax'),
      dataIndex: 'fax',
      key: 'fax',
      width: 130,
      render: (fax: string | null) => fax || '-',
    },
    {
      title: tContacts('table.email'),
      dataIndex: 'email',
      key: 'email',
      width: 200,
      sorter: true,
      showSorterTooltip: false,
      render: (email: string) => email || '-',
    },
    {
      title: tContacts('table.address'),
      key: 'address',
      width: 300,
      ellipsis: true,
      render: (_: unknown, record: MasterDataContactListItem) => (
        <ContactAddressDisplay contact={record} />
      ),
    },
    {
      title: t('common:createdAt'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 130,
      render: (createdAt: string) =>
        createdAt ? <TimestampDisplay date={createdAt} showLabel={false} /> : '-',
    },
    {
      title: t('common:updatedAt'),
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      width: 130,
      render: (updatedAt: string) =>
        updatedAt ? <TimestampDisplay date={updatedAt} showLabel={false} /> : '-',
    },
    {
      title: tContacts('table.action'),
      key: 'action',
      width: 80,
      fixed: 'right',
      align: 'center',
      render: (_: unknown, record: MasterDataContactListItem) => (
        <Dropdown
          menu={{
            items: [
              {
                key: 'setPrimary',
                label: t('vendor.actions.setPrimary'),
                icon: <CheckOutlined />,
                disabled: record.isPrimary || !record.isActive,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleSetPrimary(record);
                },
              },
              {
                key: 'edit',
                label: t('vendor.actions.edit'),
                icon: <EditOutlined />,
                disabled: !record.isActive || loadingContactDetail,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleEdit(record);
                },
              },
              {
                type: 'divider',
              },
              {
                key: 'toggle-status',
                label: record.isActive
                  ? t('vendor.actions.deactivate')
                  : t('vendor.actions.activate'),
                icon: record.isActive ? <StopOutlined /> : <CheckCircleOutlined />,
                danger: record.isActive === true,
                style: !record.isActive ? { color: token.colorSuccess } : undefined,
                disabled: record.isPrimary && record.isActive,
                onClick: (e) => {
                  e.domEvent.stopPropagation();
                  handleToggleStatus(record);
                },
              },
            ],
          }}
          trigger={['click']}
        >
          <Button type="text" icon={<MoreOutlined />} onClick={(e) => e.stopPropagation()} />
        </Dropdown>
      ),
    },
  ];

  // Count active filters (exclude typeId; count isActive only when explicitly not true)
  const activeFilterCount =
    (filters.isPrimary !== null ? 1 : 0) + (filters.isActive !== true ? 1 : 0);

  return (
    <div className="tw-px-6 tw-pb-8">
      {/* Production Enhanced Table Styling */}
      <style>
        {`
          /* Production Enhanced - Tabular numbers for all tables */
          .production-enhanced .ant-table-cell {
            font-variant-numeric: tabular-nums;
            font-feature-settings: 'tnum';
          }

          /* Consistent 40px row height */
          .production-enhanced .ant-table-tbody > tr > td {
            height: 40px;
            min-height: 40px;
          }

          /* Title case headers (no transformation) */
          .production-enhanced .ant-table-thead > tr > th {
            font-weight: ${token.fontWeightStrong};
          }

          /* Inactive row styling - uniform dimmed appearance */
          /* Must target both tr AND td because Ant Design sets background on cells */
          .production-enhanced .ant-table-tbody > tr.inactive-row,
          .production-enhanced .ant-table-tbody > tr.inactive-row > td {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row:hover > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:active > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row:focus > td {
            background-color: ${token.colorFillQuaternary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row > td:hover {
            background-color: ${token.colorFillQuaternary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td,
          .production-enhanced .ant-table-tbody > tr.inactive-row.ant-dropdown-open > td:hover {
            background-color: ${token.colorBgLayout} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row td {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row a {
            color: ${token.colorTextTertiary} !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:hover,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:focus,
          .production-enhanced .ant-table-tbody > tr.inactive-row .ant-btn:active {
            color: ${token.colorTextTertiary} !important;
            background: transparent !important;
            border-color: transparent !important;
          }

          .production-enhanced .ant-table-tbody > tr.inactive-row .anticon {
            color: ${token.colorTextTertiary} !important;
          }

          /* Minimal tab styles */
          .contact-minimal-tab {
            cursor: pointer;
            padding-bottom: 8px;
            font-size: ${token.fontSizeLG}px;
            transition: all 0.2s ease;
            border-bottom: 2px solid transparent;
            color: ${token.colorText};
            font-weight: 400;
          }
          .contact-minimal-tab:hover {
            color: ${token.colorPrimary};
            border-bottom-color: ${token.colorPrimaryBorder};
          }
          .contact-minimal-tab[data-active="true"] {
            color: ${token.colorPrimary};
            font-weight: 600;
            border-bottom-color: ${token.colorPrimary};
          }
        `}
      </style>

      {/* Header Section */}
      <div className="tw-mb-6">
        <h1 style={{ fontSize: token.fontSizeHeading2, fontWeight: token.fontWeightStrong }}>
          {tContacts('list.title')}
        </h1>
      </div>

      {/* Tabs + Search + Filter + Create - All in one line */}
      <div className="tw-mb-6 tw-flex tw-items-center tw-gap-6">
        {/* Minimal Tabs */}
        <Space size={24}>
          <span
            onClick={() => handleTabChange('1')}
            className="contact-minimal-tab"
            data-active={filters.typeId === 1}
          >
            {tContacts('tabs.vendor')}
          </span>
          <span
            onClick={() => handleTabChange('2')}
            className="contact-minimal-tab"
            data-active={filters.typeId === 2}
          >
            {tContacts('tabs.customer')}
          </span>
        </Space>

        {/* Search */}
        <Input
          placeholder={tContacts('list.searchPlaceholder')}
          value={filters.search}
          onChange={(e) => setSearch(e.target.value)}
          variant="outlined"
          size="large"
          suffix={
            <SearchOutlined
              style={{
                fontSize: token.sizeMD,
                color: token.colorPrimary,
                cursor: 'pointer',
                transition: 'all 0.3s ease',
              }}
            />
          }
          style={{
            width: '450px',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
            transition: 'all 0.3s ease',
          }}
          styles={{
            input: {
              borderRadius: radiusTokens.card,
            },
          }}
        />

        {/* Filter */}
        <ContactFilterPopover
          open={filterPopoverOpen}
          onOpenChange={setFilterPopoverOpen}
          onApply={handleFilterApply}
          initialFilters={{
            isPrimary: filters.isPrimary,
            isActive: filters.isActive,
          }}
        >
          <Badge count={activeFilterCount}>
            <Button
              icon={<FilterOutlined style={{ color: token.colorPrimary }} />}
              size="large"
              onClick={() => setFilterPopoverOpen(true)}
              style={{
                borderRadius: radiusTokens.card,
                height: `${controlHeightTokens.controlHeightXL}px`,
                width: `${controlHeightTokens.controlHeightXL}px`,
                border: `1px solid ${token.colorBorder}`,
                transition: 'all 0.3s ease',
              }}
              styles={{
                icon: { fontSize: token.sizeMD },
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.borderColor = token.colorPrimary;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = token.colorBorder;
              }}
            />
          </Badge>
        </ContactFilterPopover>

        {/* Create Button */}
        <Button
          type="primary"
          icon={<PlusOutlined />}
          size="large"
          onClick={() => {
            setEditingContact(null);
            setModalVisible(true);
          }}
          style={{
            marginLeft: 'auto',
            borderRadius: radiusTokens.card,
            height: `${controlHeightTokens.controlHeightXL}px`,
            paddingLeft: token.paddingLG,
            paddingRight: token.paddingLG,
          }}
        >
          {tContacts('list.create')}
        </Button>
      </div>

      {/* Table */}
      <TableContainer>
        <Table<MasterDataContactListItem>
          {...tableProps}
          columns={columns}
          rowKey="contactId"
          rowClassName={(record) => (!record.isActive ? 'inactive-row' : '')}
          loading={isLoading}
          showSorterTooltip={false}
          onRow={(record) => ({
            onClick: () => {
              // Navigate to detail page with type info
              const entityId = record.vendorId || record.customerId;
              const type = record.vendorId ? 1 : 2;
              navigate(`/auth/contacts/${record.contactId}?entityId=${entityId}&type=${type}`);
            },
            style: {
              cursor: 'pointer',
            },
          })}
          locale={{
            emptyText: (
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: `${token.paddingXL * 2}px ${token.paddingXL}px`,
                  textAlign: 'center',
                }}
              >
                <InboxOutlined
                  style={{
                    fontSize: token.sizeXXL * 2,
                    color: token.colorTextDisabled,
                    marginBottom: token.marginLG,
                  }}
                />
                <Title
                  level={3}
                  style={{
                    marginBottom: filters.search || activeFilterCount > 0 ? 0 : token.marginLG,
                    color: token.colorText,
                  }}
                >
                  {filters.search || activeFilterCount > 0
                    ? tContacts('list.emptyWithFiltersTitle')
                    : tContacts('list.emptyNoDataTitle')}
                </Title>
                {!filters.search && activeFilterCount === 0 && (
                  <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    size="large"
                    style={{ height: `${controlHeightTokens.controlHeightXL}px` }}
                    onClick={() => {
                      setEditingContact(null);
                      setModalVisible(true);
                    }}
                  >
                    {tContacts('list.createFirst')}
                  </Button>
                )}
              </div>
            ),
          }}
          pagination={{
            ...tableProps.pagination,
            showSizeChanger: false,
          }}
          scroll={{ x: 1400 }}
        />
      </TableContainer>

      {/* Show error if fetch failed */}
      {error && (
        <div style={{ marginTop: token.margin, color: token.colorError }}>
          {tContacts('list.fetchFailed')}: {error.message}
        </div>
      )}

      {/* Contact Form Modal */}
      <MasterDataContactFormModal
        visible={modalVisible}
        onClose={() => {
          setModalVisible(false);
          setEditingContact(null);
          setEditingListRecord(null);
        }}
        onSuccess={() => {
          refresh();
          setEditingContact(null);
          setEditingListRecord(null);
        }}
        contact={editingContact}
        listRecord={editingListRecord}
        defaultTypeId={filters.typeId}
      />

      {/* Confirmation Dialog for Toggle Status */}
      <ConfirmationDialog
        open={toggleStatusDialog.isOpen}
        onClose={toggleStatusDialog.close}
        onConfirm={async () => {
          const contactData = toggleStatusDialog.data;
          if (contactData) {
            setToggling(true);
            try {
              const newStatus = !contactData.isActive;
              if (contactData.vendorId) {
                await toggleVendorContactStatus(
                  contactData.vendorId,
                  contactData.contactId,
                  newStatus
                );
              } else if (contactData.customerId) {
                await toggleCustomerContactStatus(
                  contactData.customerId,
                  contactData.contactId,
                  newStatus
                );
              }
              message.success(
                t('vendor.messages.contactActionSuccess', {
                  action: newStatus
                    ? t('vendor.status.active').toLowerCase()
                    : t('vendor.status.inactive').toLowerCase(),
                })
              );
              await refresh();
              toggleStatusDialog.close();
            } catch (error) {
              message.error(t('vendor.messages.statusUpdateFailed'));
              console.error('Error toggling contact status:', error);
            } finally {
              setToggling(false);
            }
          }
        }}
        title={
          toggleStatusDialog.data?.isActive
            ? tContacts('modals.deactivateContactTitle')
            : tContacts('modals.activateContactTitle')
        }
        message={t('vendor.messages.confirmStatusChange', {
          action: toggleStatusDialog.data?.isActive
            ? t('vendor.actions.deactivate').toLowerCase()
            : t('vendor.actions.activate').toLowerCase(),
          name: toggleStatusDialog.data?.name || '',
        })}
        type={toggleStatusDialog.data?.isActive ? 'danger' : 'info'}
        confirmText={
          toggleStatusDialog.data?.isActive
            ? t('vendor.actions.deactivate')
            : t('vendor.actions.activate')
        }
        loading={toggling}
      />

      {/* Confirmation Dialog for Set Primary */}
      <ConfirmationDialog
        open={setPrimaryDialog.isOpen}
        onClose={() => {
          setPrimaryDialog.close();
          setExistingPrimaryForSetPrimary(null);
        }}
        onConfirm={async () => {
          const contactData = setPrimaryDialog.data;
          if (contactData) {
            setToggling(true);
            try {
              // First unset the existing primary if any
              if (existingPrimaryForSetPrimary) {
                if (contactData.vendorId) {
                  await setVendorContactPrimary(
                    contactData.vendorId,
                    existingPrimaryForSetPrimary.contactId,
                    false
                  );
                } else if (contactData.customerId) {
                  await setCustomerContactPrimary(
                    contactData.customerId,
                    existingPrimaryForSetPrimary.contactId,
                    false
                  );
                }
              }

              // Then set the new contact as primary
              if (contactData.vendorId) {
                await setVendorContactPrimary(contactData.vendorId, contactData.contactId, true);
              } else if (contactData.customerId) {
                await setCustomerContactPrimary(
                  contactData.customerId,
                  contactData.contactId,
                  true
                );
              }
              message.success(t('vendor.messages.contactSetAsPrimary'));
              await refresh();
              setPrimaryDialog.close();
              setExistingPrimaryForSetPrimary(null);
            } catch (error) {
              message.error(t('vendor.messages.primaryContactUpdateFailed'));
              console.error('Error setting primary contact:', error);
            } finally {
              setToggling(false);
            }
          }
        }}
        title={
          existingPrimaryForSetPrimary
            ? t('vendor.modals.primaryContactExists')
            : t('vendor.modals.setPrimaryContact')
        }
        message={
          existingPrimaryForSetPrimary
            ? t('vendor.modals.replacePrimaryContact', {
                current: existingPrimaryForSetPrimary.name,
                new: setPrimaryDialog.data?.name || '',
              })
            : t('vendor.modals.setPrimaryContactConfirm')
        }
        type="info"
        confirmText={
          existingPrimaryForSetPrimary
            ? t('vendor.modals.replacePrimary')
            : t('vendor.actions.confirm')
        }
        loading={toggling}
      />
    </div>
  );
};

export default ContactListPage;
